import { Button, Paper, Typography, Divider, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Box } from "@mui/system";
import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUser, tConvert } from "../../../../Utils/helper";
import { getEmployeeCurrentMonthAttendance } from "../../../../Redux/Actions/Attendance";
import CheckIn from "../../../../Asssets/Images/checkIn.svg";
import CheckOut from "../../../../Asssets/Images/checkOut2.svg";
import In from "../../../../Asssets/Images/In.svg";
import Out from "../../../../Asssets/Images/Out.svg";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TimerIcon from "@mui/icons-material/Timer";
import InfoIcon from "@mui/icons-material/Info";
import { RiPencilFill } from "react-icons/ri";

// Helper function to format seconds to HH:MM:SS
const formatSecondsToTime = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

// Helper function to parse HH:MM:SS to seconds
const parseTimeToSeconds = (timeStr) => {
  if (!timeStr || timeStr === "-") return 0;
  const parts = timeStr.split(":");
  if (parts.length !== 3) return 0;
  const [hours, minutes, seconds] = parts.map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};

// Ramadan date range: expected hours 8 full / 4 half inside range, 9 full / 4.5 half outside
const RAMADAN_START_DATE = new Date(2026, 1, 19);
RAMADAN_START_DATE.setHours(0, 0, 0, 0);
const RAMADAN_END_DATE = new Date(2026, 2, 20);
RAMADAN_END_DATE.setHours(0, 0, 0, 0);

const isDateInRamadanRange = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getTime() >= RAMADAN_START_DATE.getTime() && d.getTime() <= RAMADAN_END_DATE.getTime();
};

const AttendanceForm = ({
  lastAttendance,
  setModalOpen,
  setCheckOutModalOpen,
  setEditCheckInRemarksModalOpen = () => {},
  showEditCheckInRemarks = false,
  classes,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [elapsedTime, setElapsedTime] = useState(0);

  // Get monthly attendance from Redux
  const { attendance: monthlyAttendance, loading: monthlyAttendanceLoading } = useSelector(
    (state) => state.currentMonthAttendance
  );

  // Fetch monthly attendance on mount (only once)
  useEffect(() => {
    if (!monthlyAttendance?.attendances && !monthlyAttendanceLoading) {
      dispatch(getEmployeeCurrentMonthAttendance());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Determine if user is currently checked in (has check_in but no check_out)
  const isCheckedIn =
    lastAttendance?.attendance?.check_in &&
    !lastAttendance?.attendance?.check_out &&
    Object.keys(lastAttendance?.attendance || {}).length !== 0;

  // Determine if last attendance has a completed shift (both check_in and check_out)
  const hasCompletedShift =
    lastAttendance?.attendance?.check_in &&
    lastAttendance?.attendance?.check_out &&
    Object.keys(lastAttendance?.attendance || {}).length !== 0;

  // Calculate completed shift time in seconds
  const calculateCompletedShiftTime = () => {
    if (!hasCompletedShift) return 0;
    const checkInTime = new Date(lastAttendance.attendance.check_in).getTime();
    const checkOutTime = new Date(lastAttendance.attendance.check_out).getTime();
    return Math.floor((checkOutTime - checkInTime) / 1000);
  };

  const completedShiftTime = hasCompletedShift ? calculateCompletedShiftTime() : 0;

  // Stopwatch effect - runs every second when checked in
  useEffect(() => {
    let interval = null;

    if (isCheckedIn) {
      const checkInTime = new Date(lastAttendance.attendance.check_in).getTime();

      // Calculate initial elapsed time
      const initialElapsed = Math.floor((Date.now() - checkInTime) / 1000);
      setElapsedTime(initialElapsed > 0 ? initialElapsed : 0);

      // Update every second
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - checkInTime) / 1000);
        setElapsedTime(elapsed > 0 ? elapsed : 0);
      }, 1000);
    } else {
      setElapsedTime(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isCheckedIn, lastAttendance?.attendance?.check_in]);

  // Calculate monthly statistics
  const calculateMonthlyStats = () => {
    const attendances = monthlyAttendance?.attendances || [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let fullDays = 0;
    let halfDays = 0;
    let totalActualSeconds = 0;
    let expectedHoursSum = 0;
    let todayCounted = false; // Track if today is already counted in the loop

    attendances.forEach((att) => {
      const status = att.status?.toLowerCase();
      const attendanceDate = new Date(att.date);
      attendanceDate.setHours(0, 0, 0, 0);
      const isToday = attendanceDate.getTime() === today.getTime();

      // Do not include holidays in Monthly Expected
      if (status === "holiday") {
        return;
      }

      // Count full days (present or late)
      if (status === "present" || status === "late") {
        fullDays += 1;
        expectedHoursSum += isDateInRamadanRange(attendanceDate) ? 8 : 9;
        if (isToday) {
          todayCounted = true;
        }
      } else if (status === "half-day") {
        // Only count as half day if there's an approved half-day leave (by both line manager and HR)
        // Otherwise, count it as a full day
        if (att.hasApprovedHalfDayLeave === true) {
          halfDays += 1;
          expectedHoursSum += isDateInRamadanRange(attendanceDate) ? 4 : 4.5;
          if (isToday) {
            todayCounted = true;
          }
        } else {
          // If status is "half-day" but no approved leave, count as full day
          fullDays += 1;
          expectedHoursSum += isDateInRamadanRange(attendanceDate) ? 8 : 9;
          if (isToday) {
            todayCounted = true;
          }
        }
      }

      // Sum actual working hours (exclude today if user is checked in, as we'll add current elapsed time)
      // Only add totalHours if it's not today OR if user is not currently checked in
      // If it's today and user is checked in, we'll add the current elapsed time instead
      if (att.totalHours && att.totalHours !== "-" && (!isToday || !isCheckedIn)) {
        totalActualSeconds += parseTimeToSeconds(att.totalHours);
      }
    });

    // If user is currently checked in and today hasn't been counted yet, add today
    if (isCheckedIn && !todayCounted) {
      // Check if today has an approved half-day leave
      const todayAttendance = attendances.find((att) => {
        const attendanceDate = new Date(att.date);
        attendanceDate.setHours(0, 0, 0, 0);
        return attendanceDate.getTime() === today.getTime();
      });

      // Do not include holidays in Monthly Expected
      if (todayAttendance?.status?.toLowerCase() === "holiday") {
        // skip
      } else
      if (todayAttendance?.hasApprovedHalfDayLeave === true) {
        halfDays += 1;
        expectedHoursSum += isDateInRamadanRange(today) ? 4 : 4.5;
      } else {
        fullDays += 1;
        expectedHoursSum += isDateInRamadanRange(today) ? 8 : 9;
      }
    }

    // If user is currently checked in, add the current elapsed time to today's total
    if (isCheckedIn && elapsedTime > 0) {
      totalActualSeconds += elapsedTime;
    }

    const expectedHours = expectedHoursSum;

    return {
      expectedHours,
      totalActualSeconds,
      fullDays,
      halfDays,
    };
  };

  const monthlyStats = calculateMonthlyStats();

  // Calculate lastCheckOutTime with proper null checks using useMemo
  const lastCheckOutTime = useMemo(() => {
    if (!lastAttendance?.attendance?.check_in) {
      return null;
    }

    const userAuth = getUser() ? getUser() || "" : null;
    if (!userAuth?.employee?.shift?.start_time) {
      return null;
    }

    try {
      const startTime = new Date(userAuth.employee.shift.start_time)
        ?.toString()
        ?.split(" ")[4]
        ?.split(":")[0];

      if (!startTime) {
        return null;
      }

      const checkInDate = new Date(lastAttendance.attendance.check_in);
      if (isNaN(checkInDate.getTime())) {
        return null;
      }

      const setStartTime = new Date(checkInDate).setHours(
        parseInt(startTime),
        0,
        0,
        0
      );

      const checkoutTime = new Date(checkInDate).setHours(
        new Date(setStartTime).getHours() + 13,
        0,
        0,
        0
      );

      return checkoutTime;
    } catch (error) {
      console.error("Error calculating lastCheckOutTime:", error);
      return null;
    }
  }, [lastAttendance?.attendance?.check_in]);

  const showCheckoutButton =
    Boolean(lastAttendance?.attendance?.check_in) &&
    !lastAttendance?.attendance?.check_out &&
    lastCheckOutTime !== null &&
    new Date().getTime() < new Date(lastCheckOutTime).getTime();

  const checkoutActionsWrapperSx = {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 1,
  };

  /** Timesheet-style gradient; default flat, hover elevation matches MUI contained Check out. */
  const editCheckInRemarksBtnSx = {
    textTransform: "none",
    padding: "10px 20px",
    background: "linear-gradient(to bottom, #0097d9, #00b1ff)",
    boxShadow: "none",
    border: "none",
    maxWidth: 260,
    width: "100%",
    minWidth: "fit-content",
    fontSize: "12px",
    fontWeight: 500,
    borderRadius: "10px",
    color: "white",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1.2,
    minHeight: "unset",
    transition: theme.transitions.create(["background-color", "box-shadow"], {
      duration: theme.transitions.duration.short,
    }),
    "&:hover": {
      background: "linear-gradient(to bottom, #0086c4, #00a2e6)",
      boxShadow: theme.shadows[4],
    },
    "& svg": {
      marginRight: "5px",
      fontSize: "17px",
      flexShrink: 0,
    },
  };

  return (
    <>
      <Box sx={{ margin: { xs: "15px 10px", sm: "20px 20px", md: "30px 40px" } }}>
        {
          <Paper
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: { xs: "column", xl: "row" },
              alignItems: { xs: "stretch", xl: "center" },
              justifyContent: "space-between",
              padding: { xs: "15px", sm: "15px 20px", md: "10px 30px" },
              boxShadow: "none",
              borderRadius: "20px",
              gap: { xs: 2, md: 3 },
              '@media (max-width: 1400px)': {
                flexDirection: 'column',
                alignItems: 'stretch'
              },
              '@media (min-width: 1400px)': {
                flexDirection: 'row',
                alignItems: 'center'
              }
            }}
          >
            {/* Wrapper for Check-in/Check-out and Button - Same row on screens below 1400px */}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'row' },
              alignItems: { xs: 'center' },
              justifyContent: 'space-between',
              width: { xs: '100%' },
              gap: { xs: 2 },
              '@media (min-width: 1400px)': {
                display: 'contents'
              }
            }}>
              {/* Check-in/Check-out Section */}
              <Box sx={{
                width: { xs: "100%" },
                '@media (min-width: 1400px)': {
                  flex: '0 0 auto',
                  width: 'auto'
                }
              }}>
                <Box sx={{ display: "flex", my: 1, alignItems: "center" }}>
                  <Box
                    component="img"
                    src={CheckIn}
                    alt="checkin"
                    sx={{
                      width: { xs: "35px", sm: "40px", md: "50px" },
                      height: { xs: "35px", sm: "40px", md: "50px" }
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: "13px", sm: "14px", md: "16px" },
                      fontWeight: "700",
                      color: "#253766",
                      margin: { xs: "0 10px", sm: "0 15px", md: "0 20px" },
                      width: { xs: "100px", sm: "120px", md: "150px" },
                    }}
                  >
                    Last Check-In
                  </Typography>
                  <Typography sx={{ fontSize: { xs: "13px", sm: "14px", md: "16px" } }}>
                    {lastAttendance?.attendance &&
                      Object.keys(lastAttendance?.attendance).length !== 0 &&
                      lastAttendance?.attendance?.check_in
                      ? tConvert(
                        new Date(lastAttendance?.attendance?.check_in)
                          .toString()
                          .split(" ")[4]
                      )
                      : "-"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", my: 1, alignItems: "center" }}>
                  <Box
                    component="img"
                    src={CheckOut}
                    alt="checkout"
                    sx={{
                      width: { xs: "35px", sm: "40px", md: "50px" },
                      height: { xs: "35px", sm: "40px", md: "50px" }
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: "13px", sm: "14px", md: "16px" },
                      fontWeight: "700",
                      color: "#253766",
                      margin: { xs: "0 10px", sm: "0 15px", md: "0 20px" },
                      width: { xs: "100px", sm: "120px", md: "150px" },
                    }}
                  >
                    Last Check-Out
                  </Typography>
                  <Typography sx={{ fontSize: { xs: "13px", sm: "14px", md: "16px" } }}>
                    {lastAttendance?.attendance &&
                      Object.keys(lastAttendance?.attendance).length !== 0 &&
                      lastAttendance?.attendance?.check_out
                      ? tConvert(
                        new Date(lastAttendance?.attendance?.check_out)
                          .toString()
                          .split(" ")[4]
                      )
                      : "-"}
                  </Typography>
                </Box>
              </Box>

              {/* Action Button Section - On same line below 1400px */}
              <Box sx={{
                display: { xs: "flex" },
                '@media (min-width: 1400px)': {
                  display: 'none'
                },
                justifyContent: { xs: "flex-end" },
                alignItems: { xs: "center" },
                alignSelf: { xs: "center" }
              }}>
                {/* {lastAttendance?.attendance.status !== "On Leave" ? : } */}
                {lastAttendance?.attendance === null ||
                  // lastAttendance.attendance.status === "on-leave" ||
                  (lastAttendance?.attendance?.check_in &&
                    lastAttendance?.attendance?.check_out) ||
                  (lastCheckOutTime !== null && new Date().getTime() > new Date(lastCheckOutTime).getTime()) ||
                  Object.keys(lastAttendance?.attendance || {}).length === 0 ? (
                  <Button
                    variant="contained"
                    className={classes.check_in_btn}
                    onClick={() => setModalOpen(true)}
                    sx={{
                      width: "auto",
                      minWidth: "fit-content",
                      whiteSpace: "nowrap",
                      py: { xs: 1, sm: 1.5 },
                      px: { xs: 2, sm: 3 },
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: { xs: 1, sm: 1.5 }
                    }}
                  >
                    <Box
                      component="img"
                      src={In}
                      alt="in"
                      sx={{ width: { xs: "24px", sm: "28px", md: "32px" }, flexShrink: 0 }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "700 !important",
                        fontSize: { xs: "13px", sm: "14px", md: "16px" },
                        whiteSpace: "nowrap"
                      }}
                    >
                      Check In
                    </Typography>
                  </Button>
                ) : showCheckoutButton ? (
                  <Box sx={checkoutActionsWrapperSx}>
                    <Button
                      variant="contained"
                      className={classes.check_out_btn}
                      onClick={() => setCheckOutModalOpen(true)}
                      sx={{
                        width: "auto",
                        minWidth: "fit-content",
                        whiteSpace: "nowrap",
                        py: { xs: 1, sm: 1.5 },
                        px: { xs: 2, sm: 3 },
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: { xs: 1, sm: 1.5 },
                      }}
                    >
                      <Box
                        component="img"
                        src={Out}
                        alt="out"
                        sx={{ width: { xs: "24px", sm: "28px", md: "32px" }, flexShrink: 0 }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "700 !important",
                          fontSize: { xs: "13px", sm: "14px", md: "16px" },
                          whiteSpace: "nowrap",
                        }}
                      >
                        Check Out
                      </Typography>
                    </Button>
                    {showEditCheckInRemarks && (
                      <Button
                        variant="contained"
                        onClick={() => setEditCheckInRemarksModalOpen(true)}
                        sx={editCheckInRemarksBtnSx}
                      >
                        <RiPencilFill aria-hidden />
                        Edit Remarks
                      </Button>
                    )}
                  </Box>
                ) : (
                  ""
                )}
              </Box>
            </Box>

            {/* Statistics Section - All 3 in a row on all screen sizes */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: { xs: 2, sm: 3, md: 4 },
                alignItems: "center",
                justifyContent: { xs: "space-around", sm: "center" },
                flex: {
                  '@media (min-width: 1400px)': '1 1 auto'
                },
                py: { xs: 1 },
                '@media (min-width: 1400px)': {
                  py: 0
                },
                borderTop: { xs: "1px solid #eee" },
                borderBottom: { xs: "1px solid #eee" },
                '@media (min-width: 1400px)': {
                  borderTop: 'none',
                  borderBottom: 'none'
                },
                width: { xs: "100%" },
                '@media (min-width: 1400px)': {
                  width: 'auto'
                },
                flexWrap: { xs: "wrap", sm: "nowrap" }
              }}
            >
              {/* Total Hours (Stopwatch) */}
              <Box sx={{
                textAlign: "center",
                minWidth: { xs: "80px", sm: "100px" }
              }}>
                <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  mb: 0.5
                }}>
                  <TimerIcon sx={{ color: "#0097d9", fontSize: { xs: 16, sm: 18, md: 20 } }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "#253766", fontSize: { xs: "10px", sm: "11px", md: "12px" } }}
                  >
                    Total Hours
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 400,
                      color: "#666",
                      fontSize: { xs: "8px", sm: "9px", md: "10px" },
                      ml: 0.5
                    }}
                  >
                    {isCheckedIn ? "(current shift)" : hasCompletedShift ? "(last shift)" : "(today)"}
                  </Typography>
                  <Tooltip
                    title={
                      isCheckedIn
                        ? "Time elapsed from check-in to current time"
                        : hasCompletedShift
                          ? "Total time worked in the last completed shift"
                          : "Total hours worked"
                    }
                    arrow
                  >
                    <InfoIcon sx={{
                      color: "#0097d9",
                      fontSize: { xs: 12, sm: 14, md: 16 },
                      cursor: "help",
                      ml: 0.5
                    }} />
                  </Tooltip>
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: "16px", sm: "18px", md: "20px" },
                    fontWeight: 700,
                    color: isCheckedIn || hasCompletedShift ? "#0097d9" : "#666",
                    fontFamily: "monospace",
                  }}
                >
                  {isCheckedIn
                    ? formatSecondsToTime(elapsedTime)
                    : hasCompletedShift
                      ? formatSecondsToTime(completedShiftTime)
                      : "-"}
                </Typography>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />

              {/* Monthly Expected Hours */}
              <Box sx={{
                textAlign: "center",
                minWidth: { xs: "80px", sm: "100px" }
              }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mb: 0.5 }}>
                  <CalendarMonthIcon sx={{ color: "#59c680", fontSize: { xs: 16, sm: 18, md: 20 } }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "#253766", fontSize: { xs: "10px", sm: "11px", md: "12px" } }}
                  >
                    Monthly Expected
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 400,
                      color: "#666",
                      fontSize: { xs: "8px", sm: "9px", md: "10px" },
                      ml: 0.5
                    }}
                  >
                    (till today)
                  </Typography>
                  <Tooltip
                    title="Expected working hours from the start of the month to today"
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: {
                          padding: '8px 12px',
                          '& .MuiTooltip-arrow': {
                            color: 'rgba(0, 0, 0, 0.87)'
                          }
                        }
                      }
                    }}
                  >
                    <InfoIcon sx={{
                      color: "#59c680",
                      fontSize: { xs: 12, sm: 14, md: 16 },
                      cursor: "help",
                      ml: 0.5
                    }} />
                  </Tooltip>
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: "16px", sm: "18px", md: "20px" },
                    fontWeight: 700,
                    color: "#59c680",
                  }}
                >
                  {monthlyStats.expectedHours.toFixed(1)}h
                </Typography>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />

              {/* Monthly Actual Hours */}
              <Box sx={{
                textAlign: "center",
                minWidth: { xs: "80px", sm: "100px" }
              }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mb: 0.5 }}>
                  <AccessTimeIcon sx={{ color: "#8C63EE", fontSize: { xs: 16, sm: 18, md: 20 } }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "#253766", fontSize: { xs: "10px", sm: "11px", md: "12px" } }}
                  >
                    Monthly Actual
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 400,
                      color: "#666",
                      fontSize: { xs: "8px", sm: "9px", md: "10px" },
                      ml: 0.5
                    }}
                  >
                    (till now)
                  </Typography>
                  <Tooltip title="Actual working hours from the start of the month till now" arrow>
                    <InfoIcon sx={{
                      color: "#8C63EE",
                      fontSize: { xs: 12, sm: 14, md: 16 },
                      cursor: "help",
                      ml: 0.5
                    }} />
                  </Tooltip>
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: "16px", sm: "18px", md: "20px" },
                    fontWeight: 700,
                    color: "#8C63EE",
                    fontFamily: "monospace",
                  }}
                >
                  {formatSecondsToTime(monthlyStats.totalActualSeconds)}
                </Typography>
              </Box>
            </Box>

            {/* Action Button Section - For screens 1400px and above */}
            <Box sx={{
              display: { xs: "none" },
              '@media (min-width: 1400px)': {
                display: 'flex',
                justifyContent: 'flex-end',
                flex: '0 0 auto'
              }
            }}>
              {/* {lastAttendance?.attendance.status !== "On Leave" ? : } */}
              {lastAttendance?.attendance === null ||
                // lastAttendance.attendance.status === "on-leave" ||
                (lastAttendance?.attendance?.check_in &&
                  lastAttendance?.attendance?.check_out) ||
                (lastCheckOutTime !== null && new Date().getTime() > new Date(lastCheckOutTime).getTime()) ||
                Object.keys(lastAttendance?.attendance || {}).length === 0 ? (
                <Button
                  variant="contained"
                  className={classes.check_in_btn}
                  onClick={() => setModalOpen(true)}
                  sx={{
                    width: "auto",
                    minWidth: "fit-content",
                    whiteSpace: "nowrap",
                    py: { xs: 1, sm: 1.5 },
                    px: { xs: 2, sm: 3 },
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: { xs: 1, sm: 1.5 }
                  }}
                >
                  <Box
                    component="img"
                    src={In}
                    alt="in"
                    sx={{ width: { xs: "24px", sm: "28px", md: "32px" }, flexShrink: 0 }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "700 !important",
                      fontSize: { xs: "13px", sm: "14px", md: "16px" },
                      whiteSpace: "nowrap"
                    }}
                  >
                    Check In
                  </Typography>
                </Button>
              ) : showCheckoutButton ? (
                <Box sx={checkoutActionsWrapperSx}>
                  <Button
                    variant="contained"
                    className={classes.check_out_btn}
                    onClick={() => setCheckOutModalOpen(true)}
                    sx={{
                      width: "auto",
                      minWidth: "fit-content",
                      whiteSpace: "nowrap",
                      py: { xs: 1, sm: 1.5 },
                      px: { xs: 2, sm: 3 },
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: { xs: 1, sm: 1.5 },
                    }}
                  >
                    <Box
                      component="img"
                      src={Out}
                      alt="out"
                      sx={{ width: { xs: "24px", sm: "28px", md: "32px" }, flexShrink: 0 }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "700 !important",
                        fontSize: { xs: "13px", sm: "14px", md: "16px" },
                        whiteSpace: "nowrap",
                      }}
                    >
                      Check Out
                    </Typography>
                  </Button>
                  {showEditCheckInRemarks && (
                    <Button
                      variant="contained"
                      onClick={() => setEditCheckInRemarksModalOpen(true)}
                      sx={editCheckInRemarksBtnSx}
                    >
                      <RiPencilFill aria-hidden />
                      Edit Remarks
                    </Button>
                  )}
                </Box>
              ) : (
                ""
              )}
            </Box>
          </Paper>
        }
      </Box>
    </>
  );
};

export default AttendanceForm;
